import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { RealtimeChannel, RealtimePostgresChangesPayload } from '@supabase/supabase-js';

type PostgresChangeEvent = 'INSERT' | 'UPDATE' | 'DELETE' | '*';

interface UseRealtimeOptions<T> {
  table: string;
  schema?: string;
  event?: PostgresChangeEvent;
  filter?: string;
  onInsert?: (payload: T) => void;
  onUpdate?: (payload: T, oldRecord?: T) => void;
  onDelete?: (oldRecord: T) => void;
  onChange?: (payload: RealtimePostgresChangesPayload<T>) => void;
  enabled?: boolean;
}

export function useRealtime<T extends Record<string, any>>({
  table,
  schema = 'public',
  event = '*',
  filter,
  onInsert,
  onUpdate,
  onDelete,
  onChange,
  enabled = true,
}: UseRealtimeOptions<T>) {
  const channelRef = useRef<RealtimeChannel | null>(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      cleanup();
      return;
    }

    // Create unique channel name
    const channelName = `realtime-${table}-${Date.now()}`;

    // Build the subscription config
    const subscriptionConfig: any = {
      event,
      schema,
      table,
    };

    if (filter) {
      subscriptionConfig.filter = filter;
    }

    // Subscribe to changes
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes' as any,
        subscriptionConfig,
        (payload: RealtimePostgresChangesPayload<T>) => {
          // Call the generic onChange handler
          onChange?.(payload);

          // Call specific event handlers
          switch (payload.eventType) {
            case 'INSERT':
              onInsert?.(payload.new as T);
              break;
            case 'UPDATE':
              onUpdate?.(payload.new as T, payload.old as T);
              break;
            case 'DELETE':
              onDelete?.(payload.old as T);
              break;
          }
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log(`Realtime subscribed to ${table}`);
        } else if (status === 'CHANNEL_ERROR') {
          console.error(`Realtime subscription error for ${table}`);
        }
      });

    channelRef.current = channel;

    return cleanup;
  }, [table, schema, event, filter, enabled, onInsert, onUpdate, onDelete, onChange, cleanup]);

  return { cleanup };
}

// Hook for subscribing to user-specific messages
export function useProactiveMessages(
  userId: string | undefined,
  onNewMessage: (message: any) => void,
  enabled = true
) {
  return useRealtime({
    table: 'proactive_messages',
    event: 'INSERT',
    filter: userId ? `user_id=eq.${userId}` : undefined,
    onInsert: onNewMessage,
    enabled: enabled && !!userId,
  });
}

// Hook for subscribing to task updates
export function useTaskUpdates(
  organizationId: string | undefined,
  onTaskChange: (task: any, eventType: string) => void,
  enabled = true
) {
  return useRealtime({
    table: 'sop_run_steps',
    event: '*',
    onChange: (payload) => {
      onTaskChange(payload.new || payload.old, payload.eventType);
    },
    enabled: enabled && !!organizationId,
  });
}

// Hook for subscribing to run updates
export function useRunUpdates(
  organizationId: string | undefined,
  onRunChange: (run: any, eventType: string) => void,
  enabled = true
) {
  return useRealtime({
    table: 'sop_runs',
    event: '*',
    onChange: (payload) => {
      onRunChange(payload.new || payload.old, payload.eventType);
    },
    enabled: enabled && !!organizationId,
  });
}
